import React, { useState, useEffect, useRef } from 'react';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import ITooltipButton from '@/components/interfaces/graphql/HtmlElement/TooltipButton';
import TooltipButtonSnippet from '@/components/common/HtmlElements/TooltipButton/Snippet';
import TooltipButton from '@/components/common/HtmlElements/TooltipButton/TooltipButton';
import useRenderExampleSentence from '@/components/admin/SlideShow/Utils/useRenderExampleSentence';
import { GlossaryTerm, SlideObject } from '../../../../types';
import './VocabCard.scss';

interface VocabCardProps {
  glossaryTerm: GlossaryTerm;
  slideObject: SlideObject;
}

const VocabCard = ({ glossaryTerm, slideObject }: VocabCardProps) => {
  const tooltipButtonRef = useRef(null);
  const locale = useSlideShowContext(state => state.locale);
  const isPresentMode = useSlideShowContext(state => state.isPresentMode);
  const renderExampleSentence = useRenderExampleSentence();
  const TooltipComponent = isPresentMode ? TooltipButton : TooltipButtonSnippet;

  const term = locale === 'en' ? glossaryTerm.term_en : glossaryTerm.term_es;
  const example = locale === 'en' ? glossaryTerm.example_sentence_en : glossaryTerm.example_sentence_es;

  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const tooltipButton = tooltipButtonRef?.current;
    if (!tooltipButton) return;

    const slideObjectContainer = tooltipButtonRef.current.closest('.tw-absolute') || tooltipButtonRef.current;
    const buttonCenterX = tooltipButton.offsetLeft + (tooltipButton.offsetWidth / 2);
    const containerLeft = slideObjectContainer.offsetLeft;
    const tooltipWidth = 290;

    setTop(tooltipButton.offsetTop + tooltipButton.offsetHeight);
    setLeft(buttonCenterX - (tooltipWidth / 2) + containerLeft);
  }, [tooltipButtonRef, imageLoaded]);

  return (
    <div className="vocab-card">
      <img
        alt={glossaryTerm.term_en}
        className="vocab-card-image"
        src={glossaryTerm.s3_url}
        onLoad={() => setImageLoaded(true)}
      />
      <span className="vocab-term text labels tw-font-bold" dangerouslySetInnerHTML={{ __html: term }} />
      <div ref={tooltipButtonRef} className="vocab-btn-wrapper">
        <TooltipComponent tooltipButton={{
          data: {
            autoHide: true,
            buttonTextEn: 'Definition',
            buttonTextEs: 'Definición',
            tooltipStyles: {
              left,
              top,
            },
          },
          htmlEn: glossaryTerm.definition_en,
          htmlEs: glossaryTerm.definition_es,
          id: glossaryTerm.id,
          modelId: slideObject.id,
          modelType: 'SlideObject',
          type: 'TooltipButton',
        } as ITooltipButton}
        />
      </div>
      <hr className="vocab-card-divider" />
      {example && (
        <div className="vocab-example">
          <span className="text labels vocab-example-label">Example:</span>
          <span className="text body vocab-example-sentence">
            {renderExampleSentence(example, term, glossaryTerm.id)}
          </span>
        </div>
      )}
    </div>
  );
};

export default VocabCard;
